import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { EnquiryStatus } from 'src/shared/models/enquiries/enquiry.model';
import { EnquiryUserApplicationModel } from 'src/shared/models/enquiries/enquiry-user-application.model';
import { EnquiryFilters } from 'src/shared/models/enquiries/enquiry-filters.model';
import { AuthenticatedHttpService } from './authenticatedHttpService.service';
import { LocalStorageService } from './local-storage.service';
import { PushProvider } from './push/push.provider';
import { NotificationType } from 'src/shared/models/push-notification/push-notification';
import { filter } from 'rxjs';
import { defaultEnquiryPageSize } from 'src/constants/defaults';
import { Order } from 'src/shared/models/common/order';
import { environment } from 'src/environments/environment';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { CreateEnquiryApplicationModel } from 'src/shared/models/enquiries/enquiry-application-create.model';
import { EnquiryMessageModel } from 'src/shared/models/enquiries/enquiry-message.model';
import * as fp from 'lodash/fp';
import { parseUtcDate } from 'src/shared/utils/date-utils';

export const EnquiryStatusDictionary = {
  [EnquiryStatus.None]: 'megaphone',
  [EnquiryStatus.PartialApproval]: 'checkmark-circle-outline',
  [EnquiryStatus.Confirmed]: 'checkmark-circle-outline',
  [EnquiryStatus.Rejected]: 'close-circle-outline',
};

export enum EnquiryApplicationStatus {
  Open = 0,
  Pending = 1,
  Registered = 2,
  Closed = 3,
}

export const EnquiryApplicationStatusDictionary = {
  [EnquiryApplicationStatus.Open]: 'megaphone',
  [EnquiryApplicationStatus.Pending]: 'hourglass-outline',
  [EnquiryApplicationStatus.Registered]: 'checkmark-circle-outline',
  [EnquiryApplicationStatus.Closed]: 'close-circle-outline',
};

export enum EnquiryApplicationSorting {
  None = 1,
  Open,
  Pending,
  Registered,
  Closed,
}

@Injectable({ providedIn: 'root' })
export class EnquiryProvider {
  private static readonly APPLICATIONS = 'ENQUIRIES_APPLICATIONS';

  private readonly _applications = signal<Array<EnquiryUserApplicationModel>>([]);
  public readonly applications$ = toObservable(this._applications);
  public readonly applications = this._applications.asReadonly();

  private readonly _sortedApplications = signal<Array<EnquiryUserApplicationModel>>([]);
  public readonly sortedApplications$ = toObservable(this._sortedApplications);
  public readonly sortedApplications = this._sortedApplications.asReadonly();

  count: number;
  total: number;
  filter: EnquiryFilters;

  constructor(
    protected http: AuthenticatedHttpService,
    protected storage: LocalStorageService,
    private pushProvider: PushProvider
  ) {
    this.pushProvider.onPushNotification$
      .pipe(filter((n) => n.type === NotificationType.EnquiryApplication))
      .subscribe(async () => {
        if (this.filter?.from && this.filter?.to) {
          await this.fetchApplications(this.filter.from, this.filter.to);
        }
      });
  }

  async fetchApplications(firstDay: Date, lastDay: Date) {
    this.filter = {
      from: firstDay,
      to: lastDay,
      order: Order.Descending,
      page: 0,
      pageSize: defaultEnquiryPageSize,
    };

    try {
      const response = await this.http.wrapIntoOffline<{
        total: number;
        items: Array<EnquiryUserApplicationModel>;
      }>({
        fetch: this.http.get,
        params: [
          `${environment.web.apiUrl}enquiries/applications?${HttpUtils.toParamsString(this.filter)}`,
        ],
        storeKey: EnquiryProvider.APPLICATIONS,
        renew: !fp.get('page', this.filter),
      });

      if (!response || !response.result) return;

      const applications = response.result.items || new Array<EnquiryUserApplicationModel>();

      this.count = applications.length;
      this.total = response.result.total;

      this._applications.set(applications);
    } catch (error) {
      console.error(error);
    }
  }

  async readExpiredApplications() {
    return new Promise(async (resolve) => {
      try {
        const response: boolean = await this.http.post(
          `${environment.web.apiUrl}enquiries/read-expired`,
          null
        );

        const isReadExpired = response;

        resolve(isReadExpired);
      } catch (e) {
        console.error(e);
        resolve(false);
      }
    });
  }

  sortApplicationsByDate(dateFrom: Date, dateTo?: Date | null) {
    const now = new Date();

    const sortedApplications = this._applications().filter((userApplication) => {
      const appDateVisible = parseUtcDate(userApplication.application.dateVisible);
      appDateVisible.setHours(0, 0, 0, 0);
      const appDateTo = parseUtcDate(userApplication.application.dateTo);

      if (appDateTo < now) return false;

      if (dateTo) {
        return appDateVisible >= dateFrom && appDateVisible <= dateTo;
      }

      return appDateVisible.getTime() === dateFrom.getTime();
    });

    this._sortedApplications.set(sortedApplications);
  }

  async read(id: string) {
    return new Promise(async (resolve) => {
      try {
        await this.http.put(`${environment.web.apiUrl}enquiries/application/${id}/read`, null);

        this.updateUserApplicationIsRead(id, true);

        resolve(true);
      } catch (e) {
        console.error(e);
        resolve(true);
      }
    });
  }

  updateUserApplicationIsRead(applicationId: string, isRead: boolean) {
    const list = this._applications();
    const index = list.findIndex((a) => a.id === applicationId);

    if (index !== -1) {
      const updatedList = [...list];
      updatedList[index] = { ...updatedList[index], isRead };
      this._applications.set(updatedList);

      // Also update sorted applications if they exist
      const sortedList = this._sortedApplications();
      const sortedIndex = sortedList.findIndex((a) => a.id === applicationId);
      if (sortedIndex !== -1) {
        const updatedSortedList = [...sortedList];
        updatedSortedList[sortedIndex] = { ...updatedSortedList[sortedIndex], isRead };
        this._sortedApplications.set(updatedSortedList);
      }
    }
  }

  async uploadApplications(page: number) {
    if (this._applications().length >= this.total) return;

    this.filter.page = page;

    const response = await this.http.wrapIntoOffline<{
      total: number;
      items: Array<EnquiryUserApplicationModel>;
    }>({
      fetch: this.http.get,
      params: [
        `${environment.web.apiUrl}enquiries/applications?${HttpUtils.toParamsString(this.filter)}`,
      ],
      storeKey: EnquiryProvider.APPLICATIONS,
      renew: !fp.get('page', this.filter),
    });

    const applications = response.result?.items || new Array<EnquiryUserApplicationModel>();
    const newUserApplicationList = [...this._applications(), ...applications];

    this._applications.set(newUserApplicationList);

    this.count = newUserApplicationList.length;
    this.total = response.result?.total!;
  }

  async createApplication(model: CreateEnquiryApplicationModel) {
    try {
      return await this.http.post(`${environment.web.apiUrl}enquiries/application`, model);
    } catch (error) {
      console.error(error);
    }
  }

  findApplication(id: string): EnquiryUserApplicationModel | undefined {
    return this._sortedApplications().find((a) => a.id === id);
  }

  async getApplication(id: string): Promise<EnquiryUserApplicationModel | null> {
    try {
      const application = await HttpUtils.handle<EnquiryUserApplicationModel>({
        request: this.http.get(`${environment.web.apiUrl}enquiries/application/${id}`),
      });

      if (!application?.enquiries) return null;

      const storedApplications = await this.storage.get(EnquiryProvider.APPLICATIONS);
      if (storedApplications && storedApplications.items.length) {
        const applications = [application, ...storedApplications.items];
        this._applications.set(applications);

        await this.storage.set(EnquiryProvider.APPLICATIONS, {
          total: this.total,
          items: applications,
        });
      } else {
        this._applications.set([application]);
        await this.storage.set(EnquiryProvider.APPLICATIONS, {
          total: this.total,
          items: [application],
        });
      }

      return application;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async updateApplication(id: string): Promise<EnquiryUserApplicationModel | null> {
    try {
      const application = await HttpUtils.handle<EnquiryUserApplicationModel>({
        request: this.http.get(`${environment.web.apiUrl}enquiries/application/${id}`),
      });

      if (!application?.enquiries) return null;

      const list = this._applications();
      const index = list.findIndex((a) => a.id === id);

      if (index !== -1) {
        const updatedList = [...list];
        updatedList[index] = { ...updatedList[index], ...application };
        this._applications.set(updatedList);
      }

      return application;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async sendMessage(applicationId: string, model: EnquiryMessageModel) {
    try {
      return await this.http.post(
        `${environment.web.apiUrl}enquiries/message/application/${applicationId}`,
        model
      );
    } catch (error) {
      console.error(error);
    }
  }

  async purge() {
    await this.storage.remove(EnquiryProvider.APPLICATIONS);
  }
}
